import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { knowledgeHubAPI, isSessionExpired, SESSION_EXPIRED_SENTINEL } from '../services/api'

// ── Types ─────────────────────────────────────────────────────────────

export interface KBCategory {
  categoryId: string
  name: string
  slug: string
  description: string
  icon: string
  sortOrder: number
  articleCount: number
  publicVisible: boolean
  status: string
  createdAt: string
  updatedAt: string
}

export interface KBArticleSummary {
  articleId: string
  title: string
  slug: string
  categoryId: string
  categoryName: string
  excerpt: string
  authorName: string
  status: string
  tags: string[]
  viewCount: number
  helpfulCount: number
  isPinned: boolean
  contentRiskLevel: string
  createdAt: string
  publishedAt?: string
}

export interface KBArticle extends KBArticleSummary {
  content: string
  authorId: string
  disclaimerRequired: boolean
  disclaimerAcceptedByAdminId?: string
  disclaimerAcceptedAt?: string
  reviewedByAdminId?: string
  reviewedAt?: string
  linkedThreadId?: string
  updatedAt: string
}

export interface KBThreadSummary {
  threadId: string
  title: string
  categoryId: string
  categoryName: string
  authorId: string
  authorDisplayName: string
  status: string
  isPinned: boolean
  replyCount: number
  upvoteCount: number
  acceptedReplyId?: string
  acceptedAt?: string
  lastReplyAt?: string
  linkedArticleId?: string
  linkedArticleTitle?: string
  createdAt: string
  updatedAt: string
}

export interface KBThread extends KBThreadSummary {
  body: string
}

export interface KBReply {
  replyId: string
  threadId: string
  body: string
  authorId: string
  authorDisplayName: string
  isOfficialAnswer: boolean
  isAccepted: boolean
  isHidden: boolean
  upvoteCount: number
  createdAt: string
  updatedAt: string
}

interface KnowledgeHubState {
  categories: KBCategory[]
  categoriesLoading: boolean
  categoriesError: string | null

  articles: KBArticleSummary[]
  articlesNextCursor: string | null
  articlesLoading: boolean
  articlesError: string | null

  currentArticle: KBArticle | null
  currentArticleLoading: boolean
  currentArticleError: string | null

  threads: KBThreadSummary[]
  threadsNextCursor: string | null
  threadsLoading: boolean
  threadsError: string | null

  currentThread: KBThread | null
  currentThreadLoading: boolean
  currentThreadError: string | null

  replies: KBReply[]
  repliesNextCursor: string | null
  repliesLoading: boolean
  repliesError: string | null
}

const initialState: KnowledgeHubState = {
  categories: [],
  categoriesLoading: false,
  categoriesError: null,

  articles: [],
  articlesNextCursor: null,
  articlesLoading: false,
  articlesError: null,

  currentArticle: null,
  currentArticleLoading: false,
  currentArticleError: null,

  threads: [],
  threadsNextCursor: null,
  threadsLoading: false,
  threadsError: null,

  currentThread: null,
  currentThreadLoading: false,
  currentThreadError: null,

  replies: [],
  repliesNextCursor: null,
  repliesLoading: false,
  repliesError: null,
}

// ── Article Thunks ────────────────────────────────────────────────────

export const fetchCategories = createAsyncThunk(
  'knowledgeHub/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const data = await knowledgeHubAPI.listCategories()
      return data.items as KBCategory[]
    } catch (err: any) {
      if (isSessionExpired(err)) return rejectWithValue(SESSION_EXPIRED_SENTINEL)
      return rejectWithValue(err.response?.data?.error || err.message)
    }
  }
)

export const fetchArticles = createAsyncThunk(
  'knowledgeHub/fetchArticles',
  async (params: { categoryId: string; cursor?: string }, { rejectWithValue }) => {
    try {
      const data = await knowledgeHubAPI.listArticles(params.categoryId, 20, params.cursor)
      return {
        items: data.items as KBArticleSummary[],
        nextCursor: data.nextCursor as string | null,
        append: !!params.cursor,
      }
    } catch (err: any) {
      if (isSessionExpired(err)) return rejectWithValue(SESSION_EXPIRED_SENTINEL)
      return rejectWithValue(err.response?.data?.error || err.message)
    }
  }
)

export const fetchArticle = createAsyncThunk(
  'knowledgeHub/fetchArticle',
  async (articleId: string, { rejectWithValue }) => {
    try {
      return await knowledgeHubAPI.getArticle(articleId) as KBArticle
    } catch (err: any) {
      if (isSessionExpired(err)) return rejectWithValue(SESSION_EXPIRED_SENTINEL)
      return rejectWithValue(err.response?.data?.error || err.message)
    }
  }
)

// ── Thread Thunks ─────────────────────────────────────────────────────

export const fetchThreads = createAsyncThunk(
  'knowledgeHub/fetchThreads',
  async (params: { categoryId: string; cursor?: string }, { rejectWithValue }) => {
    try {
      const data = await knowledgeHubAPI.listThreads(params.categoryId, 20, params.cursor)
      return {
        items: data.items as KBThreadSummary[],
        nextCursor: data.nextCursor as string | null,
        append: !!params.cursor,
      }
    } catch (err: any) {
      if (isSessionExpired(err)) return rejectWithValue(SESSION_EXPIRED_SENTINEL)
      return rejectWithValue(err.response?.data?.error || err.message)
    }
  }
)

export const fetchThread = createAsyncThunk(
  'knowledgeHub/fetchThread',
  async (threadId: string, { rejectWithValue }) => {
    try {
      return await knowledgeHubAPI.getThread(threadId) as KBThread
    } catch (err: any) {
      if (isSessionExpired(err)) return rejectWithValue(SESSION_EXPIRED_SENTINEL)
      return rejectWithValue(err.response?.data?.error || err.message)
    }
  }
)

export const fetchReplies = createAsyncThunk(
  'knowledgeHub/fetchReplies',
  async (params: { threadId: string; cursor?: string }, { rejectWithValue }) => {
    try {
      const data = await knowledgeHubAPI.listReplies(params.threadId, 20, params.cursor)
      return {
        items: data.items as KBReply[],
        nextCursor: data.nextCursor as string | null,
        append: !!params.cursor,
      }
    } catch (err: any) {
      if (isSessionExpired(err)) return rejectWithValue(SESSION_EXPIRED_SENTINEL)
      return rejectWithValue(err.response?.data?.error || err.message)
    }
  }
)

// ── Slice ─────────────────────────────────────────────────────────────

const knowledgeHubSlice = createSlice({
  name: 'knowledgeHub',
  initialState,
  reducers: {
    clearArticles(state) {
      state.articles = []
      state.articlesNextCursor = null
    },
    clearCurrentArticle(state) {
      state.currentArticle = null
      state.currentArticleError = null
    },
    clearThreads(state) {
      state.threads = []
      state.threadsNextCursor = null
    },
    clearCurrentThread(state) {
      state.currentThread = null
      state.currentThreadError = null
      state.replies = []
      state.repliesNextCursor = null
    },
    addReplyOptimistic(state, action) {
      state.replies.push(action.payload)
      if (state.currentThread) {
        state.currentThread.replyCount += 1
      }
    },
    updateThreadVote(state, action) {
      const { threadId, upvoteCount } = action.payload
      if (state.currentThread && state.currentThread.threadId === threadId) {
        state.currentThread.upvoteCount = upvoteCount
      }
      const idx = state.threads.findIndex(t => t.threadId === threadId)
      if (idx >= 0) {
        state.threads[idx].upvoteCount = upvoteCount
      }
    },
    updateReplyVote(state, action) {
      const { replyId, upvoteCount } = action.payload
      const idx = state.replies.findIndex(r => r.replyId === replyId)
      if (idx >= 0) {
        state.replies[idx].upvoteCount = upvoteCount
      }
    },
  },
  extraReducers: (builder) => {
    // Categories
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.categoriesLoading = true
        state.categoriesError = null
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categoriesLoading = false
        state.categories = action.payload
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.categoriesLoading = false
        if (action.payload !== SESSION_EXPIRED_SENTINEL) {
          state.categoriesError = action.payload as string
        }
      })

    // Articles list
    builder
      .addCase(fetchArticles.pending, (state) => {
        state.articlesLoading = true
        state.articlesError = null
      })
      .addCase(fetchArticles.fulfilled, (state, action) => {
        state.articlesLoading = false
        if (action.payload.append) {
          state.articles = [...state.articles, ...action.payload.items]
        } else {
          state.articles = action.payload.items
        }
        state.articlesNextCursor = action.payload.nextCursor
      })
      .addCase(fetchArticles.rejected, (state, action) => {
        state.articlesLoading = false
        if (action.payload !== SESSION_EXPIRED_SENTINEL) {
          state.articlesError = action.payload as string
        }
      })

    // Single article
    builder
      .addCase(fetchArticle.pending, (state) => {
        state.currentArticleLoading = true
        state.currentArticleError = null
      })
      .addCase(fetchArticle.fulfilled, (state, action) => {
        state.currentArticleLoading = false
        state.currentArticle = action.payload
      })
      .addCase(fetchArticle.rejected, (state, action) => {
        state.currentArticleLoading = false
        if (action.payload !== SESSION_EXPIRED_SENTINEL) {
          state.currentArticleError = action.payload as string
        }
      })

    // Threads list
    builder
      .addCase(fetchThreads.pending, (state) => {
        state.threadsLoading = true
        state.threadsError = null
      })
      .addCase(fetchThreads.fulfilled, (state, action) => {
        state.threadsLoading = false
        if (action.payload.append) {
          state.threads = [...state.threads, ...action.payload.items]
        } else {
          state.threads = action.payload.items
        }
        state.threadsNextCursor = action.payload.nextCursor
      })
      .addCase(fetchThreads.rejected, (state, action) => {
        state.threadsLoading = false
        if (action.payload !== SESSION_EXPIRED_SENTINEL) {
          state.threadsError = action.payload as string
        }
      })

    // Single thread
    builder
      .addCase(fetchThread.pending, (state) => {
        state.currentThreadLoading = true
        state.currentThreadError = null
      })
      .addCase(fetchThread.fulfilled, (state, action) => {
        state.currentThreadLoading = false
        state.currentThread = action.payload
      })
      .addCase(fetchThread.rejected, (state, action) => {
        state.currentThreadLoading = false
        if (action.payload !== SESSION_EXPIRED_SENTINEL) {
          state.currentThreadError = action.payload as string
        }
      })

    // Replies
    builder
      .addCase(fetchReplies.pending, (state) => {
        state.repliesLoading = true
        state.repliesError = null
      })
      .addCase(fetchReplies.fulfilled, (state, action) => {
        state.repliesLoading = false
        if (action.payload.append) {
          state.replies = [...state.replies, ...action.payload.items]
        } else {
          state.replies = action.payload.items
        }
        state.repliesNextCursor = action.payload.nextCursor
      })
      .addCase(fetchReplies.rejected, (state, action) => {
        state.repliesLoading = false
        if (action.payload !== SESSION_EXPIRED_SENTINEL) {
          state.repliesError = action.payload as string
        }
      })
  },
})

export const {
  clearArticles,
  clearCurrentArticle,
  clearThreads,
  clearCurrentThread,
  addReplyOptimistic,
  updateThreadVote,
  updateReplyVote,
} = knowledgeHubSlice.actions
export default knowledgeHubSlice.reducer
